/**
 * Client-side documents rail: the files an admin shared with the client
 * (upon approval). Selecting a file renders it on the report page's main
 * area — same preview stack as the admin experience (docx→PDF, pdf, csv,
 * image) but full-page rather than in a dialog.
 */

import { Anchor, Badge, Button, Group, ScrollArea, Stack, Text } from '@mantine/core';

import type { BackofficeFile } from '@/types/report';
import { FILE_TYPE_COLORS } from './FilePreview';

interface ClientDocumentsPanelProps {
  files: BackofficeFile[];
  /** The file currently rendered on the main page, if any. */
  viewing: BackofficeFile | null;
  /** Render this file on the main page (null = back to the report). */
  onView: (file: BackofficeFile | null) => void;
}

export function ClientDocumentsPanel({
  files,
  viewing,
  onView,
}: Readonly<ClientDocumentsPanelProps>) {
  return (
    <Stack gap="xs" style={{ minHeight: 0, flex: 1 }}>
      <Text size="xs" c="dimmed">
        Documents shared with you alongside this report.
      </Text>
      {viewing && (
        <Button
          size="compact-xs"
          variant="light"
          onClick={() => onView(null)}
          data-testid="back-to-report"
        >
          Back to the report
        </Button>
      )}
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Stack gap={6}>
          {files.map((file) => (
            <Stack
              key={file.raw_data_key}
              gap={4}
              p="xs"
              style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
              data-testid={`client-document-${file.filename}`}
            >
              <Group gap="xs" wrap="nowrap" justify="space-between">
                <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
                  {file.filename}
                </Text>
                <Group gap={6} wrap="nowrap">
                  {file.kind === 'overflow' && (
                    <Badge
                      color="indigo"
                      variant="light"
                      size="sm"
                      data-testid={`full-list-badge-${file.filename}`}
                    >
                      Full list
                    </Badge>
                  )}
                  <Badge color={FILE_TYPE_COLORS[file.file_type]} variant="light" size="sm">
                    {file.file_type.toUpperCase()}
                  </Badge>
                </Group>
              </Group>
              <Group gap="xs">
                <Button
                  size="compact-xs"
                  variant={viewing?.raw_data_key === file.raw_data_key ? 'filled' : 'default'}
                  onClick={() => onView(file)}
                  data-testid={`view-client-document-${file.filename}`}
                >
                  View
                </Button>
                <Anchor href={file.presigned_url} size="xs" download={file.filename}>
                  Download
                </Anchor>
              </Group>
            </Stack>
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
